import React from "react";
import { Globe2, Megaphone, PenLine } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type JourneySource = "meta" | "web" | "manual";

const SOURCES: Array<{
  value: JourneySource;
  label: string;
  description: string;
  icon: LucideIcon;
}> = [
  {
    value: "meta",
    label: "Meta Lead Ads",
    description: "Realtime μέσω Lineweb Lead Hub",
    icon: Megaphone,
  },
  {
    value: "web",
    label: "Φόρμα site",
    description: "Δημόσια ασφαλής υποβολή",
    icon: Globe2,
  },
  {
    value: "manual",
    label: "Χειροκίνητο lead",
    description: "Καταχώρηση από το admin",
    icon: PenLine,
  },
];

interface JourneySourceSelectorProps {
  value: JourneySource;
  onChange: (value: JourneySource) => void;
}

export function JourneySourceSelector({ value, onChange }: JourneySourceSelectorProps) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" aria-labelledby="journey-source-heading">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-sky-600">Σημείο εισόδου</p>
          <h2 id="journey-source-heading" className="mt-1 text-base font-bold tracking-tight text-slate-950">Διάλεξε πηγή για να δεις τη διαδρομή</h2>
        </div>
        <p className="text-xs leading-5 text-slate-500">Μετά την παραλαβή, όλες οι πηγές ακολουθούν τους ίδιους κανόνες.</p>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-3" role="group" aria-label="Πηγή lead">
        {SOURCES.map((source) => {
          const Icon = source.icon;
          const selected = value === source.value;
          return (
            <button
              key={source.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(source.value)}
              className={cn(
                "flex min-h-[4.75rem] items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2",
                selected
                  ? "border-slate-950 bg-slate-950 text-white shadow-md"
                  : "border-slate-200 bg-slate-50/60 text-slate-700 hover:border-slate-300 hover:bg-white",
              )}
            >
              <span className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                selected ? "bg-white/10 text-white" : "bg-white text-slate-500 shadow-sm ring-1 ring-slate-200",
              )}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-bold">{source.label}</span>
                <span className={cn("mt-0.5 block text-xs leading-4", selected ? "text-slate-300" : "text-slate-500")}>
                  {source.description}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
